import React, { useMemo, useEffect, useState, useRef } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    Typography,
    Paper,
    Skeleton,
} from '@mui/material';
import { CustomTransComponent } from '../../../components/CustomTransComponent';
import { useTheme } from '@mui/material/styles';
import EditNotificationsIcon from '@mui/icons-material/EditNotifications';
import { NotificationLog } from '@brightlayer-ui/icons-mui';
import { WarningAmberRounded } from '@mui/icons-material';
import NotificationAddIcon from '@mui/icons-material/NotificationAdd';
import { ThresholdModal } from '../../DeviceDetails/ThresholdModal';
import {
    useGetLoadTrendConfigurationQuery,
    useGetLoadTrendUnitsQuery,
} from '@fiji/common/src/features/deviceManagement/ontologyApi';
import { useGetThresholdQuery } from '@fiji/common/src/features/deviceManagement/deviceApi';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';
import { useTypedSelector } from '@fiji/common/src/app/store';
import { useRBAC } from 'hooks/useRbac';
import {
    useGetAutomationsByThresholdMutation,
    useGetAllEventByDeviceTypeIdQuery,
} from '@fiji/common/src/features/loadBalancing/loadBalancingApi';
interface TriggerEvent {
    id: string;
    name: string;
    onSet: boolean;
    onClear: boolean;
    editable?: boolean;
    channelId?: string;
    thresholdName?: string;
    unit?: string;
    severity?: string;
}

interface SelectTriggerEventProps {
    selectedTriggerEvents?: TriggerEvent[];
    onTriggerEventChange?: (events: TriggerEvent[]) => void;
    onCheckboxStateChange?: (hasChecked: boolean) => void;
    initialTriggerEvents?: TriggerEvent[];
    deviceTypeId?: string;
    deviceTypeIds?: string[];
    selectedId?: string;
    deviceId?: string;
    isLoadGroup?: boolean;
}

const DEFAULT_UNITS: Record<string, string> = {
    voltage: 'V',
    current: 'A',
    power: 'W',
};

const STANDARD_STATES = [
    { prefix: 'Low', suffix: 'Critical', key: 'lowCritical' },
    { prefix: 'Low', suffix: 'Warning', key: 'lowWarning' },
    { prefix: 'High', suffix: 'Warning', key: 'highWarning' },
    { prefix: 'High', suffix: 'Critical', key: 'highCritical' },
];

export type { TriggerEvent };

const ThresholdActionIcon: React.FC<{
    event: TriggerEvent;
    deviceId: string;
    canEdit: boolean;
    canView: boolean;
    onOpen: (event: TriggerEvent, mode: 'view' | 'edit') => void;
    thresholdName: string;
}> = ({ event, deviceId, canEdit, canView, onOpen, thresholdName }) => {
    const theme = useTheme();
    const { data: thresholdData, isLoading } = useGetThresholdQuery(
        { channelKey: event.channelId, deviceId },
        { skip: !event.channelId || !deviceId }
    );

    if (!event.editable || isLoading) return null;

    // Find the SPECIFIC threshold by its name
    const specificThreshold = thresholdData?.data?.threshold?.find((t: any) => t.name === thresholdName);

    // Check if THIS specific threshold exists and is enabled
    const thresholdExists = specificThreshold?.isEnabled ?? false;

    // Render icon based on THIS specific threshold's status
    if (thresholdExists) {
        if (canEdit) {
            return (
                <EditNotificationsIcon
                    onClick={() => onOpen(event, 'edit')}
                    sx={{
                        fontSize: 20,
                        color: theme.palette.primary.main,
                        cursor: 'pointer',
                    }}
                />
            );
        }
        if (canView) {
            return (
                <NotificationLog
                    onClick={() => onOpen(event, 'view')}
                    sx={{
                        fontSize: 20,
                        color: theme.palette.primary.main,
                        cursor: 'pointer',
                    }}
                />
            );
        }
    } else {
        if (canEdit) {
            return (
                <NotificationAddIcon
                    onClick={() => onOpen(event, 'edit')}
                    sx={{
                        fontSize: 20,
                        color: theme.palette.primary.main,
                        cursor: 'pointer',
                    }}
                />
            );
        }
    }
    return null;
};

export const SelectTriggerEvent: React.FC<SelectTriggerEventProps> = ({
    onTriggerEventChange,
    onCheckboxStateChange,
    initialTriggerEvents,
    deviceTypeId,
    deviceTypeIds,
    selectedId,
    deviceId,
    isLoadGroup,
}) => {
    const theme = useTheme();
    const thresholdModalRef = useRef<any>(null);
    const loadTrendDeviceTypeIds = useMemo(() => (deviceTypeId ? [deviceTypeId] : []), [deviceTypeId]);

    const {
        data: configResponse,
        isLoading: isConfigLoading,
        isError: isConfigError,
    } = useGetLoadTrendConfigurationQuery({ deviceTypeId: deviceTypeId! }, { skip: !deviceTypeId });
    const {
        data: unitsResponse,
        isLoading: isUnitsLoading,
        isError: isUnitsError,
    } = useGetLoadTrendUnitsQuery(loadTrendDeviceTypeIds, {
        skip: !loadTrendDeviceTypeIds.length || isLoadGroup,
    });

    const payload = {
        deviceTypeIds: deviceTypeIds || [],
        includeOnlyLoadEvents: !isLoadGroup,
        includeOnlyLoadGroupEvents: !!isLoadGroup,
        page: 0,
        size: 10,
    };

    const {
        data: eventsResponse,
        isLoading: isEventsLoading,
        isError: isEventsError,
    } = useGetAllEventByDeviceTypeIdQuery(
        {
            params: { all: false },
            payload: payload,
        },
        { skip: !deviceTypeIds?.length }
    );

    const [triggerEvents, setTriggerEvents] = useState<TriggerEvent[]>([]);

    const THRESHOLD_KEYS = new Set(['voltage', 'power', 'current']);

    const permissions = useTypedSelector(selectCurrentPermission);

    const { hasPermission } = useRBAC(permissions);
    const canUpdateThreshold = hasPermission('device-details_edit-devices');
    const canManageThreshold = hasPermission('telemetry-thresholds_view-devices');

    const [automations, setAutomations] = useState<any[]>([]);
    const [thresholdModalMode, setThresholdModalMode] = useState<'view' | 'edit'>('edit');

    const [getAutomations] = useGetAutomationsByThresholdMutation();

    const dynamicEvents = useMemo(() => {
        const staticEvents: TriggerEvent[] = (eventsResponse?.data?.records || []).map((event: any) => ({
            id: event.id,
            name: event.displayName || event.name,
            onSet: false,
            onClear: false,
        }));

        if (isLoadGroup) return staticEvents;

        if (!configResponse?.data || !unitsResponse?.data || !selectedId) return [...staticEvents];

        const configMap = configResponse?.data?.[deviceTypeId ?? ''] ?? {};
        const unitsMap = unitsResponse.data;

        const generatedEvents = Object.entries(unitsMap).flatMap(([unitKey, unitLabel]) => {
            const loadUuid = selectedId.includes('_') ? selectedId.split('_').pop() : selectedId;
            const configKey = `${loadUuid}_${unitKey}`;
            const channelId = configMap[configKey];

            if (!channelId) return [];

            const unitSymbol = DEFAULT_UNITS[unitKey] || '';
            const isEditable = (canUpdateThreshold || canManageThreshold) && THRESHOLD_KEYS.has(unitKey);
            const displayName =
                typeof unitLabel === 'string'
                    ? unitLabel
                    : (unitLabel as any)?.label || unitKey.charAt(0).toUpperCase() + unitKey.slice(1);

            return STANDARD_STATES.map((state) => ({
                id: `${configKey}_${state.prefix.toLowerCase()}_${state.suffix.toLowerCase()}`,
                name: `${state.prefix} ${displayName} ${state.suffix}`,
                onSet: false,
                onClear: false,
                editable: isEditable,
                channelId: channelId,
                unit: unitSymbol,
                thresholdName: state.key,
            }));
        });

        return [...staticEvents, ...generatedEvents];
    }, [configResponse, unitsResponse, selectedId, isLoadGroup, eventsResponse]);

    useEffect(() => {
        if (dynamicEvents.length > 0) {
            const mergedEvents = dynamicEvents.map((event) => {
                const initial = initialTriggerEvents?.find((e) => {
                    if (e.id === event.id) return true;

                    // Match by channelId and specific state for thresholds
                    if (e.channelId && event.channelId && e.channelId === event.channelId) {
                        const initialName = (e.name || '').toLowerCase();
                        const severity = (e.severity || '').toLowerCase();

                        const isLow = initialName.includes('low');
                        const isHigh = initialName.includes('high');
                        const isWarning = initialName.includes('warning') || severity === 'warning';
                        const isCritical = initialName.includes('critical') || severity === 'critical';

                        const eventIsLow = (event.thresholdName || '').toLowerCase().includes('low');
                        const eventIsHigh = (event.thresholdName || '').toLowerCase().includes('high');
                        const eventIsWarning = (event.thresholdName || '').toLowerCase().includes('warning');
                        const eventIsCritical = (event.thresholdName || '').toLowerCase().includes('critical');

                        if (
                            isLow === eventIsLow &&
                            isHigh === eventIsHigh &&
                            isWarning === eventIsWarning &&
                            isCritical === eventIsCritical
                        ) {
                            return true;
                        }
                    }

                    return false;
                });

                if (initial) {
                    return { ...event, onSet: initial.onSet, onClear: initial.onClear };
                }

                return event;
            });

            setTriggerEvents(mergedEvents);
            onTriggerEventChange?.(mergedEvents);

            const hasAnyChecked = mergedEvents.some((event) => event.onSet || event.onClear);
            onCheckboxStateChange?.(hasAnyChecked);
        }
    }, [dynamicEvents, initialTriggerEvents]);

    const handleCheckboxChange = (id: string, field: 'onSet' | 'onClear'): void => {
        const updatedEvents = triggerEvents.map((event) =>
            event.id === id ? { ...event, [field]: !event[field] } : event
        );
        setTriggerEvents(updatedEvents);
        onTriggerEventChange?.(updatedEvents);

        const hasAnyChecked = updatedEvents.some((event) => event.onSet || event.onClear);
        onCheckboxStateChange?.(hasAnyChecked);
    };

    const handleOpenThresholdModal = async (event: TriggerEvent, mode: 'view' | 'edit' = 'edit'): Promise<void> => {
        setThresholdModalMode(mode);

        try {
            const response = await getAutomations({
                deviceId: deviceId || '',
                thresholdId: event.channelId || '',
            }).unwrap();
            setAutomations(response?.data || []);
        } catch (error) {
            console.error('Error fetching automations:', error);
        }

        thresholdModalRef.current?.onClick({
            channelId: event.channelId || 'unknown',
            displayName: event.name,
            unit: event.unit || '',
            value: 0,
            deviceId: deviceId || '',
        });
    };

    const hasEnabledAutomations = automations?.some((a: any) => a.isEnabled);

    const isLoading = isConfigLoading || isUnitsLoading || isEventsLoading;

    return (
        <Box sx={{ width: '100%', position: 'relative' }}>
            <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    maxHeight: 650,
                    overflow: 'auto',
                }}
            >
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell
                                sx={{
                                    fontWeight: 600,
                                    bgcolor: theme.palette.background.paper,
                                    width: '50%',
                                }}
                            >
                                <CustomTransComponent translationKey={'WIDGETS:BALANCING.TRIGGER_EVENT'} />
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{
                                    fontWeight: 600,
                                    bgcolor: theme.palette.background.paper,
                                    width: '20%',
                                }}
                            >
                                <CustomTransComponent translationKey={'WIDGETS:BALANCING.ON_SET'} />
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{
                                    fontWeight: 600,
                                    bgcolor: theme.palette.background.paper,
                                    width: '20%',
                                }}
                            >
                                <CustomTransComponent translationKey={'WIDGETS:BALANCING.ON_CLEAR'} />
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{
                                    fontWeight: 600,
                                    bgcolor: theme.palette.background.paper,
                                    width: '5%',
                                }}
                            ></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            Array.from(new Array(5)).map((_, index) => (
                                <TableRow key={index}>
                                    <TableCell>
                                        <Skeleton variant="text" width="80%" />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Skeleton variant="rectangular" width={20} height={20} sx={{ mx: 'auto' }} />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Skeleton variant="rectangular" width={20} height={20} sx={{ mx: 'auto' }} />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Skeleton variant="circular" width={20} height={20} sx={{ mx: 'auto' }} />
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : isConfigError || isUnitsError || isEventsError ? (
                            <TableRow>
                                <TableCell colSpan={4}>
                                    <Box sx={{ p: 4, textAlign: 'center' }}>
                                        <Typography variant="body2" color="error">
                                            <CustomTransComponent
                                                translationKey={'WIDGETS:BALANCING.ERROR_LOADING_EVENTS'}
                                            />
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : (
                            triggerEvents.map((event) => (
                                <TableRow
                                    key={event.id}
                                    sx={{
                                        '&:hover': {
                                            bgcolor: theme.palette.action.hover,
                                        },
                                    }}
                                >
                                    <TableCell>
                                        <Typography variant="body2">{event.name}</Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Checkbox
                                            checked={event.onSet}
                                            onChange={() => handleCheckboxChange(event.id, 'onSet')}
                                            size="small"
                                            sx={{
                                                '&.Mui-checked': {
                                                    color: theme.palette.primary.main,
                                                },
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Checkbox
                                            checked={event.onClear}
                                            onChange={() => handleCheckboxChange(event.id, 'onClear')}
                                            size="small"
                                            sx={{
                                                '&.Mui-checked': {
                                                    color: theme.palette.primary.main,
                                                },
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <ThresholdActionIcon
                                            event={event}
                                            deviceId={deviceId || ''}
                                            canEdit={canUpdateThreshold}
                                            canView={canManageThreshold}
                                            onOpen={handleOpenThresholdModal}
                                            thresholdName={event.thresholdName || ''}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <ThresholdModal
                ref={thresholdModalRef}
                mode={thresholdModalMode}
                subHeader={<CustomTransComponent translationKey={'WIDGETS:BALANCING.THRESHOLD_MODAL_SUBHEADER'} />}
                warningBanner={
                    hasEnabledAutomations ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                bgcolor: '#f9a825',
                                color: '#fff',
                                borderRadius: 1,
                                px: 2,
                                py: 1,
                                mb: 2,
                            }}
                        >
                            <WarningAmberRounded sx={{ fontSize: 20 }} />
                            <Typography variant="body2" fontWeight={500}>
                                <CustomTransComponent
                                    translationKey={'WIDGETS:BALANCING.DISABLING_THRESHOLD_WARNING'}
                                />
                            </Typography>
                        </Box>
                    ) : undefined
                }
            />
        </Box>
    );
};
